import React from 'react';
import { CONSTANTS } from '@tpco/tpco-saturn-types';
import { PvsCampaign, PvsVertical, PvsCreative } from 'models/interfaces';
import { Form, Formik } from 'formik';
import { StackedText, StackedSelect } from '@tpco/blueprint-react';
import PvsCreativeContainer, {
  SAVE_CREATIVE_MUTATION,
  TOGGLE_ACTIVE_CREATIVE_MUTATION,
  LIST_CREATIVES_BY_CAMPAIGN_QUERY
} from 'containers/PvsCreativeContainer';
import PvsCreativeEdit from './PvsCreativeEdit';
import './PvsCampaignEdit.scss';
import PvsCreativeList from './PvsCreativeList';
import { ApolloConsumer } from 'react-apollo';
import PvsCreativeView from './PvsCreativeView';
import { ApolloClient } from 'apollo-client';
import { toast } from 'react-toastify';
import { iso8601 } from 'util/dateUtils';

interface PvsCampaignEditProps {
  tgramsId: string;
  campaignId: string;
  adServerCampaignId: string;
  companyName: string;
  campaignStartDate: string;
  campaignEndDate: string;
  // pvs: PvsCampaign;
  name: string;
  weight: number;
  verticalId: string;
  lastSyncDate: string;
  isDirty: boolean;
  isActive: boolean;
  verticals: PvsVertical[];
  onVerticalIdChange(name: string): void;
  onCampaignChange(childCampaign: any): void;
}

interface PvsCampaignEditState {
  name: string;
  verticalId: string;
  isActive: boolean;
  isSaving: boolean;
  editCreative: boolean;
  viewCreative: boolean;
  creative?: PvsCreative;
  // localPvs: PvsCampaign;
}

class PvsCampaignEdit extends React.Component<PvsCampaignEditProps, PvsCampaignEditState> {
  constructor(props: PvsCampaignEditProps) {
    super(props);

    // console.log('props.isActive', props.isActive);

    let name = props.name;

    if (!props.name) {
      name = `pvs--[page]--${props.tgramsId}`;
    }

    this.state = {
      name,
      verticalId: props.verticalId,
      isActive: props.isActive,
      isSaving: false,
      creative: undefined,
      editCreative: false,
      viewCreative: false
    };
  }

  componentDidMount = () => {
    // inform parent of current state
    this.onCampaignChange();
  };

  renderCreativeEdits = () => {
    const { verticalId } = this.state;

    if (!verticalId) {
      return null;
    }

    return (
      <PvsCreativeContainer pvs_campaign_id={this.props.campaignId}>
        {({ creatives, refetch }) => {
          console.log('renderCreativeEdits creatives: ', creatives);
          const { campaignId, companyName, tgramsId, verticals } = this.props;
          const { editCreative, viewCreative, creative } = this.state;

          const vertical = verticals.find(x => `${x.vertical_id}` === verticalId);
          let verticalDisplayName = '';
          let verticalSnakeName = '';

          if (vertical) {
            verticalDisplayName = vertical.display_name;
            verticalSnakeName = vertical.ad_server_name;
          }

          return (
            <div className="card edit-campaign__container">
              <h4 className="edit-campaign__title">Creatives</h4>
              {editCreative ? (
                <PvsCreativeEdit
                  campaignId={campaignId}
                  companyName={companyName}
                  tgramsId={tgramsId}
                  verticalId={verticalId}
                  verticalDisplayName={verticalDisplayName}
                  verticalSnakeName={verticalSnakeName}
                  creativeId={creative ? creative.pvs_creative_id : ''}
                  name={creative ? creative.name : ''}
                  clickUrl={creative ? creative.click_url : ''}
                  bucketKey={creative ? creative.bucket_key : ''}
                  isActive={creative ? creative.is_active : false}
                  platformId={creative ? creative.platform_id : 1}
                  imageExists={creative ? !!creative.bucket_key === true : false}
                  onEditFinished={() => this.onCreativeEditFinished(refetch)}
                />
              ) : viewCreative ? (
                <PvsCreativeView
                  name={creative ? creative.name : ''}
                  platformId={creative ? creative.platform_id : 0}
                  pvsCreativeId={creative ? creative.pvs_creative_id : ''}
                  onViewFinished={this.onCreativeViewFinished}
                />
              ) : (
                <React.Fragment>
                  <ApolloConsumer>
                    {client => (
                      <PvsCreativeList
                        creatives={creatives}
                        onCreativeView={this.onCreativeView}
                        onCreativeEdit={this.onCreativeEdit}
                        onCreativeToggleActive={(creative: PvsCreative) =>
                          this.onCreativeToggleActive(creative, client)
                        }
                        onCreativeDuplicate={(creative: PvsCreative) =>
                          this.onCreativeDuplicate(creative, client)
                        }
                      />
                    )}
                  </ApolloConsumer>
                  <div className="pvs-creative__add-container">
                    <button
                      className="btn btn-primary pvs-creative__add"
                      onClick={this.onAddCreative}
                    >
                      Add New
                    </button>
                  </div>
                </React.Fragment>
              )}
            </div>
          );
        }}
      </PvsCreativeContainer>
    );
  };

  onCreativeEditFinished = (refetch: () => void) => {
    // trigger refresh of list

    // stop edit
    this.setState(
      {
        editCreative: false,
        creative: undefined
      },
      refetch
    );
  };

  onCreativeViewFinished = () => {
    // trigger refresh of list

    // stop edit
    this.setState({
      viewCreative: false,
      creative: undefined
    });
  };

  onAddCreative = () => {
    console.log('onAddCreative fired');
    const creative = {} as PvsCreative;
    creative.pvs_campaign_id = this.props.campaignId;

    this.setState({ editCreative: true, creative });
  };

  onCreativeEdit = (creative: PvsCreative) => {
    console.log('Edit creative: ', creative);
    this.setState({ editCreative: true, creative: creative });
  };

  onCreativeDuplicate = async (creative: PvsCreative, client: ApolloClient<any>) => {
    console.log('Duplicate creative: ', creative);

    // make creative sql data
    const duplicateCreative = {
      pvs_creative_id: null,
      pvs_campaign_id: creative.pvs_campaign_id,
      ad_server_parent_id: '',
      name: `Copy of ${creative.name}`,
      platform_id: parseInt(creative.platform_id.toString(), 10),
      click_url: creative.click_url,
      bucket_key: null,
      is_active: false,
      last_sync_date: null,
      is_dirty: true
    };

    const saveResponse = await client.mutate({
      mutation: SAVE_CREATIVE_MUTATION,
      variables: { creative: duplicateCreative },
      refetchQueries: [
        {
          query: LIST_CREATIVES_BY_CAMPAIGN_QUERY,
          variables: {
            pvs_campaign_id: this.props.campaignId
          }
        }
      ]
    });

    console.log('duplicate saveResponse: ', saveResponse);

    toast.success('Creative successfully duplicated');
  };

  onCreativeToggleActive = async (creative: PvsCreative, client: ApolloClient<any>) => {
    console.log('Toggle is_active for creative: ', creative);

    // You can't set a creative active without uploading a file
    if (!creative.bucket_key && !creative.is_active) {
      toast.error("Can't activate creative without uploading a file.");
      return;
    }

    const saveResponse = await client.mutate({
      mutation: TOGGLE_ACTIVE_CREATIVE_MUTATION,
      variables: { pvs_creative_id: creative.pvs_creative_id, is_active: !creative.is_active },
      refetchQueries: [
        {
          query: LIST_CREATIVES_BY_CAMPAIGN_QUERY,
          variables: {
            pvs_campaign_id: this.props.campaignId
          }
        }
      ]
    });

    console.log('toggleIsActivesaveResponse: ', saveResponse);

    toast.success(`Creative successfully ${!creative.is_active === false ? 'de' : ''}activated`);
  };

  onCreativeView = (creative: PvsCreative) => {
    console.log('View creative: ', creative);
    this.setState({ viewCreative: true, creative });
  };

  /**
   * Triggered when vertical dropdown changes, this function is responsible
   * for setting the internal name state and calling the local onCampaignChange
   */
  onVerticalChange = ({ value }: any) => {
    this.setState({ verticalId: value });

    const vertical = this.props.verticals.find(x => x.vertical_id === value);

    if (vertical) {
      this.props.onVerticalIdChange(vertical.display_name);
      this.setState(
        { name: `pvs--${vertical.ad_server_name}--${this.props.tgramsId}--1` },
        this.onCampaignChange
      );
    } else {
      this.props.onVerticalIdChange('[page]');
      this.setState({ name: `pvs--[page]--${this.props.tgramsId}--1` }, this.onCampaignChange);
    }
  };

  /**
   * Triggered when the pvs campaign name changes, this function is responsible
   * for setting the internal name state and calling the local onCampaignChange
   */
  onNameChange = ({ value }: any) => {
    this.setState({ name: value }, this.onCampaignChange);
  };

  /**
   * Responsible for building a PvsCampaign object from props and state
   */
  buildPvsCampaign = () => {
    const {
      isActive,
      isDirty,
      adServerCampaignId,
      lastSyncDate,
      campaignId,
      tgramsId,
      campaignEndDate,
      campaignStartDate
    } = this.props;

    const { verticalId, name } = this.state;

    // set defaults if value is missing
    const weight = typeof this.props.weight === 'number' ? this.props.weight : CONSTANTS.DEFAULT_PVS_WEIGHT;
    const is_active = typeof isActive === 'boolean' ? isActive : false;
    const is_dirty = typeof isDirty === 'boolean' ? isDirty : false;
    const ad_server_campaign_id = typeof adServerCampaignId === 'string' ? adServerCampaignId : '';
    const last_sync_date = typeof lastSyncDate === 'string' ? lastSyncDate : '';

    // construct PvsCampaign object
    const pvs: PvsCampaign = {
      pvs_campaign_id: campaignId,
      tgrams_id: tgramsId,
      ad_server_campaign_id,
      vertical_id: verticalId,
      weight,
      ad_server_name: name,
      start_date: iso8601(campaignStartDate),
      end_date: iso8601(campaignEndDate),
      is_active,
      last_sync_date,
      is_dirty
    };

    return pvs;
  };

  /**
   * This function should be called whenever the properties of the PvsCampaign that
   * this component represents change. It is responsible for building a pvs campaign
   * object from props and state, and sending that object to its parent via the
   * onCampaignChange prop.
   */
  onCampaignChange = () => {
    // build ps campaign object from state and props
    const pvs = this.buildPvsCampaign();

    // send pvs campaign object to parent
    this.props.onCampaignChange(pvs);
  };

  /**
   * This lifecycle method will check this components' props for changes
   * that affect the PvsCampaign object this component represents and will
   * trigger a local onCampaignChange if any are found.
   */
  componentDidUpdate = (prevProps: PvsCampaignEditProps) => {
    console.log('componentDidUpdate fired');
    let campaignHasChanged = false;

    // when the following props change, we need to rebuild the PvsCampaign
    // and inform the parent
    if (this.props.weight !== prevProps.weight) {
      campaignHasChanged = true;
    } else if (this.props.campaignStartDate !== prevProps.campaignStartDate) {
      campaignHasChanged = true;
    } else if (this.props.campaignEndDate !== prevProps.campaignEndDate) {
      campaignHasChanged = true;
    }

    if (campaignHasChanged) {
      this.onCampaignChange();
    }
  };

  componentWillUnmount = () => {
    // clear campaignModule cache
    this.props.onCampaignChange(null);
  };

  render() {
    const { verticals } = this.props;
    const { verticalId, name } = this.state;

    let verticalSelectOptions: any = [];

    if (verticals) {
      verticalSelectOptions = verticals.map(x => {
        return {
          value: x.vertical_id,
          name: x.display_name
        };
      });
    }

    return (
      <div>
        <Formik
          initialValues={{ verticalId, name }}
          onSubmit={values => console.log('Submitting PvsCampaignEdit: ', values)}
          enableReinitialize={true}
        >
          {() => (
            <Form className="card edit-campaign__container">
              <h4 className="edit-campaign__title">Premium Vertical Sponsorship Configuration</h4>
              <StackedSelect
                htmlId="edit-pvs-vertical"
                fieldName="verticalId"
                labelText="Vertical"
                className="form-group__col-flex-column saturn-form-group saturn-col saturn-flex-column"
                defaultOption={<option value={''}>- Select -</option>}
                options={verticalSelectOptions}
                onChange={this.onVerticalChange}
              />
              <StackedText
                htmlId="edit-pvs-name"
                className="form-group__col-flex-column saturn-form-group saturn-col saturn-flex-column"
                fieldName="name"
                labelText="Name"
                onChange={this.onNameChange}
              />
            </Form>
          )}
        </Formik>
        {this.renderCreativeEdits()}
      </div>
    );
  }
}

export default PvsCampaignEdit;
